import React from "react";
import { Heading } from "./heading";
import { clsx } from "clsx";
import { Text } from "./text";
import { Link, LinkProps } from "./link";
import { ChevronRightIcon } from "@heroicons/react/16/solid";

/**
 * A single breadcrumb entry. `to` is optional — leaf entries (the current page)
 * usually omit it and render as plain text.
 */
export type Breadcrumb = {
    label: string;
    to?: LinkProps["href"];
    params?: LinkProps["params"];
};

/**
 * The properties for {@link HeadingLayout}
 */
export type HeadingLayoutProps = {
    /** The text for the heading */
    heading: string;

    /** Optional description text under the heading */
    headingDescription?: string;

    /** Additional children that will be displayed in the heading */
    headingChildren?: Array<React.ReactNode> | React.ReactNode;

    /** Optional breadcrumb trail rendered above the heading. */
    breadcrumbs?: Array<Breadcrumb>;

    /** Everything below the heading */
    children?: React.ReactNode;

    /** Set additional classes */
    className?: string;
};

function Breadcrumbs({ items }: { items: Array<Breadcrumb> }) {
    return (
        <nav aria-label="Breadcrumb" className={"flex items-center gap-1 text-sm/5 text-zinc-500 dark:text-zinc-400"}>
            {items.map((item, idx) => {
                const isLast = idx === items.length - 1;
                return (
                    <React.Fragment key={`${idx}-${item.label}`}>
                        {item.to && !isLast ? (
                            <Link
                                href={item.to}
                                params={item.params}
                                className={
                                    "rounded px-0.5 hover:text-zinc-700 focus:outline-2 focus:outline-offset-2 focus:outline-(--color-brand-500) dark:hover:text-zinc-200"
                                }
                            >
                                {item.label}
                            </Link>
                        ) : (
                            <span
                                className={clsx("px-0.5", isLast && "font-medium text-zinc-700 dark:text-zinc-200")}
                                aria-current={isLast ? "page" : undefined}
                            >
                                {item.label}
                            </span>
                        )}
                        {!isLast && <ChevronRightIcon className={"size-3.5 shrink-0 opacity-50"} />}
                    </React.Fragment>
                );
            })}
        </nav>
    );
}

/**
 * A layout that includes a top level heading
 */
export default function HeadingLayout(props: HeadingLayoutProps) {
    return (
        <div className={clsx("flex flex-col gap-8", props.className)}>
            <div className={"flex flex-col gap-3"}>
                {props.breadcrumbs && props.breadcrumbs.length > 0 && <Breadcrumbs items={props.breadcrumbs} />}
                <div
                    className={clsx(
                        // Base
                        "flex w-full flex-wrap items-end justify-between gap-4 border-b pb-6",
                        // Colors
                        "border-zinc-950/10 dark:border-white/10",
                    )}
                >
                    <div className={"flex flex-col gap-3"}>
                        <Heading>{props.heading}</Heading>
                        {props.headingDescription && <Text>{props.headingDescription}</Text>}
                    </div>
                    {props.headingChildren !== undefined ? (
                        <div className={"flex gap-4"}>{props.headingChildren}</div>
                    ) : undefined}
                </div>
            </div>
            {props.children}
        </div>
    );
}
